import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Upload, Trash2, FileText, X, ChevronDown, ChevronUp } from 'lucide-react';
import { attachmentsApi, type Attachment, type AttachmentEntityType } from '@/lib/api';
import { useAuth } from '@/lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AttachmentsPanelProps {
  entityType: AttachmentEntityType;
  entityId:   string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/40 hover:bg-black/60"
        onClick={onClose}
      >
        <X size={20} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-xl object-contain"
        onClick={e => e.stopPropagation()}
      />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function AttachmentsPanel({ entityType, entityId }: AttachmentsPanelProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [expanded, setExpanded]               = useState(true);
  const [uploadError, setUploadError]         = useState<string | null>(null);
  const [uploading, setUploading]             = useState(false);
  const [lightboxSrc, setLightboxSrc]         = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt]         = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [dragOver, setDragOver]               = useState(false);

  const queryKey = ['attachments', entityType, entityId];

  const { data: attachments = [] } = useQuery({
    queryKey,
    queryFn: () => attachmentsApi.list(entityType, entityId),
    enabled: !!entityId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => attachmentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setConfirmDeleteId(null);
    },
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    setUploadError(null);
    setUploading(true);
    try {
      await attachmentsApi.upload(entityType, entityId, file);
      queryClient.invalidateQueries({ queryKey });
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function canDelete(att: Attachment): boolean {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'supervisor') return true;
    return att.uploaded_by === user.id;
  }

  return (
    <>
      {lightboxSrc && (
        <Lightbox src={lightboxSrc} alt={lightboxAlt} onClose={() => setLightboxSrc(null)} />
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <button
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
          onClick={() => setExpanded(e => !e)}
        >
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Paperclip size={15} className="text-slate-400" />
            Attachments ({attachments.length})
          </div>
          {expanded ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
        </button>

        {expanded && (
          <div className="border-t border-slate-100 px-5 py-4 flex flex-col gap-4">

            {/* ── Upload zone ───────────────────────────────────────────────── */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              className={`border-2 border-dashed rounded-lg px-4 py-5 text-center transition-colors ${
                dragOver ? 'border-brand-400 bg-brand-50' : 'border-slate-200 bg-slate-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf,.xlsx,.xls,.docx,image/*"
                className="hidden"
                onChange={e => handleFiles(e.target.files)}
              />
              {uploading ? (
                <p className="text-sm text-slate-400">Uploading…</p>
              ) : (
                <>
                  <Upload size={20} className="mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500 mb-2">
                    Drag & drop or{' '}
                    <button
                      className="text-brand-600 underline underline-offset-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      browse
                    </button>
                  </p>
                  <p className="text-xs text-slate-400">JPG, PNG, WEBP, PDF, XLSX, DOCX — max 25 MB</p>
                </>
              )}
            </div>

            {/* ── Upload error ──────────────────────────────────────────────── */}
            {uploadError && (
              <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {uploadError}
              </p>
            )}

            {/* ── Attachment list ───────────────────────────────────────────── */}
            {attachments.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-2">No attachments yet.</p>
            ) : (
              <div className="flex flex-col gap-2">

                {/* Image thumbnails */}
                {attachments.filter(a => isImage(a.mime_type)).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.filter(a => isImage(a.mime_type)).map(att => (
                      <div key={att.id} className="relative group">
                        <img
                          src={attachmentsApi.fileUrl(att.id)}
                          alt={att.filename}
                          className="w-20 h-20 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => { setLightboxSrc(attachmentsApi.fileUrl(att.id)); setLightboxAlt(att.filename); }}
                        />
                        {canDelete(att) && (
                          confirmDeleteId === att.id ? (
                            <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center gap-1">
                              <button
                                className="text-xs text-white bg-red-600 rounded px-1.5 py-0.5"
                                onClick={() => deleteMutation.mutate(att.id)}
                              >
                                Yes
                              </button>
                              <button
                                className="text-xs text-white bg-slate-600 rounded px-1.5 py-0.5"
                                onClick={() => setConfirmDeleteId(null)}
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              className="absolute top-1 right-1 p-1 bg-black/40 rounded-md text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => setConfirmDeleteId(att.id)}
                            >
                              <Trash2 size={12} />
                            </button>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Document list */}
                {attachments.filter(a => !isImage(a.mime_type)).map(att => (
                  <div key={att.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                    <FileText size={18} className="text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <a
                        href={attachmentsApi.fileUrl(att.id)}
                        {...(att.mime_type === 'application/pdf'
                          ? { target: '_blank', rel: 'noopener noreferrer' }  // PDFs open inline in a new tab
                          : { download: att.filename })}                       // Word/Excel download
                        className="text-sm font-medium text-brand-600 hover:underline truncate block"
                      >
                        {att.filename}
                      </a>
                      <p className="text-xs text-slate-400">{formatBytes(att.file_size)}</p>
                    </div>
                    {canDelete(att) && (
                      confirmDeleteId === att.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            className="text-xs text-white bg-red-600 rounded px-2 py-1"
                            onClick={() => deleteMutation.mutate(att.id)}
                          >
                            Delete
                          </button>
                          <button
                            className="text-xs text-slate-600 bg-slate-100 rounded px-2 py-1"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="text-slate-400 hover:text-red-500 transition-colors p-1"
                          onClick={() => setConfirmDeleteId(att.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
